import * as XLSX from "xlsx";

/**
 * Validate and sanitize input data and columns
 * @param {Array} data - Array of data items
 * @param {Array} columns - Array of column definitions
 * @returns {Object} - Validation result with sanitized data
 */
const validateAndSanitizeInputs = (data, columns) => {
  const errors = [];

  // Validate data array
  if (!Array.isArray(data)) {
    errors.push("Data must be an array");
    data = [];
  }

  // Validate columns array
  if (!Array.isArray(columns)) {
    errors.push("Columns must be an array");
    columns = [];
  }

  // Validate column structure
  const validColumns = columns.filter((col) => {
    if (!col || typeof col !== "object") return false;
    if (!col.id || !col.name) return false;
    return true;
  });

  if (validColumns.length !== columns.length) {
    errors.push(`${columns.length - validColumns.length} invalid columns were filtered out`);
  }

  // Sanitize column names to prevent injection attacks
  const sanitizedColumns = validColumns.map((col) => ({
    ...col,
    name:
      String(col.name)
        .replace(/[<>\"'&]/g, "")
        .trim() || `Column_${col.id}`,
    id: String(col.id).trim(),
  }));

  return {
    data: data.filter((item) => item !== null && item !== undefined),
    columns: sanitizedColumns,
    errors,
    isValid: errors.length === 0,
  };
};

/**
 * Safely extract value from data item with fallback handling
 * @param {Object} item - Data item
 * @param {Object} column - Column definition
 * @returns {*} - Extracted value
 */
const safeExtractValue = (item, column) => {
  try {
    // Try multiple access patterns
    let value = item.data ? item.data[column.id] : item[column.id];

    // Handle nested object access
    if (value === undefined && column.id.includes(".")) {
      const keys = column.id.split(".");
      value = item;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }
    }

    return value;
  } catch (error) {
    console.warn(`Error extracting value for column ${column.id}:`, error);
    return null;
  }
};

/**
 * Escape CSV value to handle commas, quotes, and newlines
 * @param {*} value - The value to escape
 * @returns {string} - Escaped CSV value
 */
const escapeCsvValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  let stringValue;
  try {
    // Handle different data types safely
    if (typeof value === "object") {
      if (value instanceof Date) {
        stringValue = value.toISOString();
      } else {
        stringValue = JSON.stringify(value);
      }
    } else {
      stringValue = String(value);
    }
  } catch (error) {
    console.warn("Error converting value to string:", error);
    stringValue = "[Invalid Data]";
  }

  // If the value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n") || stringValue.includes("\r")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Convert filtered data to CSV format
 * @param {Array} filteredData - Array of data items with data property
 * @param {Array} columns - Array of column definitions
 * @returns {Object} - Result object with CSV content and metadata
 */
export const convertToCSV = (filteredData, columns) => {
  const validation = validateAndSanitizeInputs(filteredData, columns);

  if (!validation.isValid) {
    console.warn("CSV conversion warnings:", validation.errors);
  }

  if (validation.data.length === 0 || validation.columns.length === 0) {
    return {
      content: "No data available",
      metadata: {
        rows: 0,
        columns: 0,
        warnings: validation.errors,
      },
    };
  }

  try {
    // Create header row using column names
    const headers = validation.columns.map((col) => col.name);
    const csvHeaders = headers.map((header) => escapeCsvValue(header)).join(",");

    // Create data rows with error handling
    const csvRows = validation.data.map((item, index) => {
      try {
        const row = validation.columns.map((col) => {
          const value = safeExtractValue(item, col);
          return escapeCsvValue(value);
        });
        return row.join(",");
      } catch (error) {
        console.warn(`Error processing row ${index}:`, error);
        // Return empty row with same number of columns
        return validation.columns.map(() => "").join(",");
      }
    });

    // Combine headers and rows
    const content = [csvHeaders, ...csvRows].join("\n");

    return {
      content,
      metadata: {
        rows: validation.data.length,
        columns: validation.columns.length,
        warnings: validation.errors,
      },
    };
  } catch (error) {
    console.error("Critical error in CSV conversion:", error);
    return {
      content: "Error: Failed to convert data to CSV format",
      metadata: {
        rows: 0,
        columns: 0,
        warnings: [...validation.errors, error.message],
      },
    };
  }
};

/**
 * Convert filtered data to Excel format
 * @param {Array} filteredData - Array of data items with data property
 * @param {Array} columns - Array of column definitions
 * @returns {Object} - Result object with Excel buffer and metadata
 */
export const convertToExcel = (filteredData, columns) => {
  const validation = validateAndSanitizeInputs(filteredData, columns);

  if (!validation.isValid) {
    console.warn("Excel conversion warnings:", validation.errors);
  }

  try {
    if (validation.data.length === 0 || validation.columns.length === 0) {
      // Create empty workbook with a message
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([["No data available"]]);
      XLSX.utils.book_append_sheet(wb, ws, "Data Export");
      return {
        buffer: XLSX.write(wb, { bookType: "xlsx", type: "array" }),
        metadata: {
          rows: 0,
          columns: 0,
          warnings: validation.errors,
        },
      };
    }

    // Prepare data for Excel
    const excelData = [];

    // Add headers
    const headers = validation.columns.map((col) => col.name);
    excelData.push(headers);

    // Add data rows with enhanced error handling
    validation.data.forEach((item, index) => {
      try {
        const row = validation.columns.map((col) => {
          let value = safeExtractValue(item, col);

          // Handle different data types for Excel
          if (value === null || value === undefined) {
            return "";
          }

          // Keep numbers as numbers for proper Excel formatting
          if (typeof value === "number" && !isNaN(value) && isFinite(value)) {
            return value;
          }

          // Handle dates
          if (value instanceof Date && !isNaN(value.getTime())) {
            return value;
          }

          // Try to parse dates from strings
          if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return date;
            }
          }

          // Handle objects and arrays
          if (typeof value === "object") {
            try {
              return JSON.stringify(value);
            } catch (jsonError) {
              console.warn(`Error stringifying object in row ${index}, column ${col.id}:`, jsonError);
              return "[Complex Object]";
            }
          }

          // Convert other types to string safely
          try {
            return String(value);
          } catch (stringError) {
            console.warn(`Error converting value to string in row ${index}, column ${col.id}:`, stringError);
            return "[Invalid Data]";
          }
        });
        excelData.push(row);
      } catch (rowError) {
        console.warn(`Error processing row ${index}:`, rowError);
        // Add empty row to maintain structure
        excelData.push(validation.columns.map(() => ""));
      }
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Calculate column widths safely
    const colWidths = headers.map((header, colIndex) => {
      try {
        const headerLength = String(header).length;
        const maxDataLength = Math.max(
          ...excelData.slice(1).map((row) => {
            const cellValue = row[colIndex];
            if (cellValue === null || cellValue === undefined) return 0;
            return String(cellValue).length;
          }),
          0
        );

        // Set reasonable width with padding, min 8, max 50
        const calculatedWidth = Math.max(headerLength, maxDataLength) + 2;
        return { wch: Math.min(Math.max(calculatedWidth, 8), 50) };
      } catch (error) {
        console.warn(`Error calculating column width for column ${colIndex}:`, error);
        return { wch: 15 }; // Default width
      }
    });

    // Apply Excel formatting safely
    try {
      ws["!autofit"] = true;
      ws["!cols"] = colWidths;
      ws["!freeze"] = { xSplit: 0, ySplit: 1 };
      ws["!autofilter"] = { ref: ws["!ref"] };

      // Make header row bold (minimal styling)
      for (let col = 0; col < headers.length; col++) {
        try {
          const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              font: { bold: true },
            };
          }
        } catch (styleError) {
          console.warn(`Error applying style to header cell ${col}:`, styleError);
        }
      }
    } catch (formatError) {
      console.warn("Error applying Excel formatting:", formatError);
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Data Export - PandaExtract");

    // Generate Excel file buffer
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    return {
      buffer,
      metadata: {
        rows: validation.data.length,
        columns: validation.columns.length,
        warnings: validation.errors,
      },
    };
  } catch (error) {
    console.error("Critical error in Excel conversion:", error);
    // Return minimal workbook with error message
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([["Error: Failed to convert data to Excel format"]]);
    XLSX.utils.book_append_sheet(wb, ws, "Error");
    return {
      buffer: XLSX.write(wb, { bookType: "xlsx", type: "array" }),
      metadata: {
        rows: 0,
        columns: 0,
        warnings: [...validation.errors, error.message],
      },
    };
  }
};

/**
 * Export filtered data as CSV file
 * @param {Array} filteredData - Array of data items
 * @param {Array} columns - Array of column definitions
 * @param {string} filename - Base filename (without extension)
 * @returns {Object} - Export result with success status and metadata
 */
export const exportAsCSV = (filteredData, columns, filename = "export") => {
  try {
    const result = convertToCSV(filteredData, columns);

    // Create blob with UTF-8 BOM for proper encoding across different systems
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + result.content], {
      type: "text/csv;charset=utf-8;",
    });

    // Create download link with enhanced error handling
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const sanitizedFilename = String(filename).replace(/[^a-z0-9\-_]/gi, "-");

    link.href = url;
    link.download = `${sanitizedFilename}-${timestamp}.csv`;

    // Ensure cleanup happens even if click fails
    const cleanup = () => {
      try {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (cleanupError) {
        console.warn("Error during cleanup:", cleanupError);
      }
    };

    document.body.appendChild(link);
    link.click();

    // Cleanup after a short delay to ensure download starts
    setTimeout(cleanup, 1000);

    return {
      success: true,
      message: "CSV export completed successfully",
      metadata: result.metadata,
    };
  } catch (error) {
    console.error("Error exporting CSV:", error);
    return {
      success: false,
      message: "Failed to export CSV: " + error.message,
      metadata: { rows: 0, columns: 0, warnings: [error.message] },
    };
  }
};

/**
 * Export filtered data as Excel file
 * @param {Array} filteredData - Array of data items
 * @param {Array} columns - Array of column definitions
 * @param {string} filename - Base filename (without extension)
 * @returns {Object} - Export result with success status and metadata
 */
export const exportAsExcel = (filteredData, columns, filename = "export") => {
  try {
    const result = convertToExcel(filteredData, columns);

    // Create blob
    const blob = new Blob([result.buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create download link with enhanced error handling
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const sanitizedFilename = String(filename).replace(/[^a-z0-9\-_]/gi, "-");

    link.href = url;
    link.download = `${sanitizedFilename}-${timestamp}.xlsx`;

    // Ensure cleanup happens even if click fails
    const cleanup = () => {
      try {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (cleanupError) {
        console.warn("Error during cleanup:", cleanupError);
      }
    };

    document.body.appendChild(link);
    link.click();

    // Cleanup after a short delay to ensure download starts
    setTimeout(cleanup, 1000);

    return {
      success: true,
      message: "Excel export completed successfully",
      metadata: result.metadata,
    };
  } catch (error) {
    console.error("Error exporting Excel:", error);
    return {
      success: false,
      message: "Failed to export Excel: " + error.message,
      metadata: { rows: 0, columns: 0, warnings: [error.message] },
    };
  }
};

/**
 * Escape tab-separated value to handle tabs, newlines, and carriage returns
 * @param {*} value - The value to escape
 * @returns {string} - Escaped tab-separated value
 */
const escapeTabSeparatedValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  let stringValue;
  try {
    // Handle different data types safely
    if (typeof value === "object") {
      if (value instanceof Date) {
        stringValue = value.toISOString();
      } else {
        stringValue = JSON.stringify(value);
      }
    } else {
      stringValue = String(value);
    }
  } catch (error) {
    console.warn("Error converting value to string for TSV:", error);
    stringValue = "[Invalid Data]";
  }

  // Replace problematic characters for tab-separated format
  return stringValue
    .replace(/\t/g, "    ") // Replace tabs with 4 spaces
    .replace(/\n/g, "\\n") // Replace newlines with escaped newlines
    .replace(/\r/g, "\\r"); // Replace carriage returns with escaped carriage returns
};

/**
 * Convert filtered data to tab-separated format (TSV) for Google Sheets
 * @param {Array} filteredData - Array of data items with data property
 * @param {Array} columns - Array of column definitions
 * @returns {Object} - Result object with TSV content and metadata
 */
const convertToTabSeparated = (filteredData, columns) => {
  const validation = validateAndSanitizeInputs(filteredData, columns);

  if (!validation.isValid) {
    console.warn("TSV conversion warnings:", validation.errors);
  }

  if (validation.data.length === 0 || validation.columns.length === 0) {
    return {
      content: "No data available",
      metadata: {
        rows: 0,
        columns: 0,
        warnings: validation.errors,
      },
    };
  }

  try {
    // Create header row using column names
    const headers = validation.columns.map((col) => col.name);
    const tabSeparatedHeaders = headers.map((header) => escapeTabSeparatedValue(header)).join("\t");

    // Create data rows with error handling
    const tabSeparatedRows = validation.data.map((item, index) => {
      try {
        const row = validation.columns.map((col) => {
          const value = safeExtractValue(item, col);
          return escapeTabSeparatedValue(value);
        });
        return row.join("\t");
      } catch (error) {
        console.warn(`Error processing row ${index} for TSV:`, error);
        // Return empty row with same number of columns
        return validation.columns.map(() => "").join("\t");
      }
    });

    // Combine headers and rows
    const content = [tabSeparatedHeaders, ...tabSeparatedRows].join("\n");

    return {
      content,
      metadata: {
        rows: validation.data.length,
        columns: validation.columns.length,
        warnings: validation.errors,
      },
    };
  } catch (error) {
    console.error("Critical error in TSV conversion:", error);
    return {
      content: "Error: Failed to convert data to tab-separated format",
      metadata: {
        rows: 0,
        columns: 0,
        warnings: [...validation.errors, error.message],
      },
    };
  }
};

/**
 * Copy filtered data to clipboard as tab-separated values (TSV) for Google Sheets
 * @param {Array} filteredData - Array of data items
 * @param {Array} columns - Array of column definitions
 * @returns {Object} - Copy result with success status and metadata
 */
export const copyToClipboard = async (filteredData, columns) => {
  try {
    const result = convertToTabSeparated(filteredData, columns);

    // Ensure document is focused before attempting clipboard operations
    if (document.hasFocus && !document.hasFocus()) {
      window.focus();
      // Give a small delay to ensure focus is established
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Try modern Clipboard API first (works in secure contexts - HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(result.content);
        console.log("✓ Data copied to clipboard using Clipboard API");
        return {
          success: true,
          message: "Data copied to clipboard successfully",
          metadata: result.metadata,
        };
      } catch (clipboardError) {
        console.warn("Modern clipboard API failed, trying fallback:", clipboardError);
        // Fall through to the fallback method
      }
    }

    // Fallback method for older browsers or non-secure contexts
    const textArea = document.createElement("textarea");
    textArea.value = result.content;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    textArea.style.opacity = "0";
    textArea.setAttribute("readonly", "");
    textArea.setAttribute("contenteditable", "true");

    document.body.appendChild(textArea);

    try {
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);

      const successful = document.execCommand("copy");
      if (!successful) {
        throw new Error("Copy command was unsuccessful");
      }

      console.log("✓ Data copied to clipboard using fallback method");
      return {
        success: true,
        message: "Data copied to clipboard successfully",
        metadata: result.metadata,
      };
    } finally {
      document.body.removeChild(textArea);
    }
  } catch (error) {
    console.error("Error copying to clipboard:", error);
    return {
      success: false,
      message: "Failed to copy to clipboard: " + error.message,
      metadata: { rows: 0, columns: 0, warnings: [error.message] },
    };
  }
};

/**
 * Get export statistics for filtered data
 * @param {Array} filteredData - Array of data items
 * @param {Array} totalData - Array of all data items (for comparison)
 * @param {Array} displayColumns - Array of column definitions being exported
 * @returns {Object} - Export statistics
 */
export const getExportStats = (filteredData, totalData, displayColumns) => {
  return {
    filteredRows: filteredData?.length || 0,
    totalRows: totalData?.length || 0,
    columns: displayColumns?.length || 0,
    isFiltered: (filteredData?.length || 0) < (totalData?.length || 0),
    percentage: totalData?.length > 0 ? Math.round(((filteredData?.length || 0) / totalData.length) * 100) : 0,
  };
};

/**
 * Generate appropriate filename based on table data and export stats
 * @param {Object} tableData - Table data object with metadata
 * @param {Object} stats - Export statistics
 * @returns {string} - Generated filename
 */
export const generateFilename = (tableData, stats) => {
  let baseName = "data-export";

  if (tableData?.name) {
    baseName = tableData.name.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  } else if (tableData?.metadata?.title) {
    baseName = tableData.metadata.title.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  } else if (tableData?.id) {
    baseName = `table-${tableData.id}`;
  }

  // Add filtered indicator if data is filtered
  if (stats.isFiltered) {
    baseName += `-filtered-${stats.percentage}pct`;
  }

  return baseName;
};

/**
 * Convert filtered data to JSON format
 * @param {Array} filteredData - Array of data items with data property
 * @param {Array} columns - Array of column definitions
 * @returns {Object} - Result object with JSON content and metadata
 */
export const convertToJSON = (filteredData, columns) => {
  const validation = validateAndSanitizeInputs(filteredData, columns);

  if (!validation.isValid) {
    console.warn("JSON conversion warnings:", validation.errors);
  }

  if (validation.data.length === 0 || validation.columns.length === 0) {
    return {
      content: JSON.stringify([], null, 2),
      metadata: {
        rows: 0,
        columns: 0,
        warnings: validation.errors,
      },
    };
  }

  try {
    // Pre-process columns to ensure unique keys
    const keyMap = new Map();
    const processedColumns = validation.columns.map((col) => {
      let sanitizedKey = col.name.toLowerCase().replace(/[^a-z0-9_]/g, "_");

      // Handle duplicate keys by adding a counter
      if (keyMap.has(sanitizedKey)) {
        const count = keyMap.get(sanitizedKey) + 1;
        keyMap.set(sanitizedKey, count);
        sanitizedKey = `${sanitizedKey}_${count}`;
      } else {
        keyMap.set(sanitizedKey, 0);
      }

      return {
        ...col,
        jsonKey: sanitizedKey,
      };
    });

    // Convert data to JSON format using unique column keys
    const jsonData = validation.data.map((item, index) => {
      try {
        const jsonItem = {};
        processedColumns.forEach((col) => {
          const value = safeExtractValue(item, col);
          // Use the unique key, handle null/undefined values
          jsonItem[col.jsonKey] = value === null || value === undefined ? null : value;
        });
        return jsonItem;
      } catch (error) {
        console.warn(`Error processing row ${index} for JSON:`, error);
        // Return empty object with column structure
        const emptyItem = {};
        processedColumns.forEach((col) => {
          emptyItem[col.jsonKey] = null;
        });
        return emptyItem;
      }
    });

    const content = JSON.stringify(jsonData, null, 2);

    // Add warnings for duplicate column names if any were found
    const duplicateWarnings = [];
    for (const [key, count] of keyMap.entries()) {
      if (count > 0) {
        duplicateWarnings.push(`Column name "${key}" had ${count + 1} duplicates - numbered suffixes added`);
      }
    }

    return {
      content,
      metadata: {
        rows: validation.data.length,
        columns: validation.columns.length,
        warnings: [...validation.errors, ...duplicateWarnings],
      },
    };
  } catch (error) {
    console.error("Critical error in JSON conversion:", error);
    return {
      content: JSON.stringify({ error: "Failed to convert data to JSON format" }, null, 2),
      metadata: {
        rows: 0,
        columns: 0,
        warnings: [...validation.errors, error.message],
      },
    };
  }
};

/**
 * Export filtered data as JSON file
 * @param {Array} filteredData - Array of data items
 * @param {Array} columns - Array of column definitions
 * @param {string} filename - Base filename (without extension)
 * @returns {Object} - Export result with success status and metadata
 */
export const exportAsJSON = (filteredData, columns, filename = "export") => {
  try {
    const result = convertToJSON(filteredData, columns);

    // Create blob
    const blob = new Blob([result.content], {
      type: "application/json;charset=utf-8;",
    });

    // Create download link with enhanced error handling
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const sanitizedFilename = String(filename).replace(/[^a-z0-9\-_]/gi, "-");

    link.href = url;
    link.download = `${sanitizedFilename}-${timestamp}.json`;

    // Ensure cleanup happens even if click fails
    const cleanup = () => {
      try {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (cleanupError) {
        console.warn("Error during cleanup:", cleanupError);
      }
    };

    document.body.appendChild(link);
    link.click();

    // Cleanup after a short delay to ensure download starts
    setTimeout(cleanup, 1000);

    return {
      success: true,
      message: "JSON export completed successfully",
      metadata: result.metadata,
    };
  } catch (error) {
    console.error("Error exporting JSON:", error);
    return {
      success: false,
      message: "Failed to export JSON: " + error.message,
      metadata: { rows: 0, columns: 0, warnings: [error.message] },
    };
  }
};
